/** A palm is one bent stem and a crown of complete feather-shaped fronds.
 * Botanical decisions are shared by both geometry levels. Broad, overlapping
 * pinnae read as a divided leaf, rather than a ring of disconnected blades. */
import * as THREE from 'three';
import { range, type Rng } from '@hyperlight/shared';
import type { BuildCtx, C3, SpeciesParams } from './types';
import { V, shade } from './primitives';
import { TREE_STEM, NO_JOINT, windJoint, type WindJoint } from './treeWind';

interface Knot { p: THREE.Vector3; radius: number }
interface Pinna { t: number; side: number; length: number; notch: number; droop: number; sweep: number; tone: number }
export interface PalmFrond {
  root: THREE.Vector3; direction: THREE.Vector3; across: THREE.Vector3;
  reach: number; rise: number; droop: number; twist: number;
  width: number; radius: number; tone: number; pinnae: Pinna[];
}
export interface PalmPlan { stem: Knot[]; fronds: PalmFrond[]; height: number; phase: number }
const PINNA_T = [.15, .27, .39, .50, .61, .72, .82, .91];
const RIB_T = [0, ...PINNA_T, 1];
const HINGE_T = .27;

/** No season/LOD random draws: a tree retains its bend and crown at distance. */
export function planPalm(rng: Rng, sp: SpeciesParams, maturity: number): PalmPlan {
  const grow = .45 + .75 * maturity, girth = .65 + .6 * maturity;
  const height = range(rng, ...sp.trunk.height) * grow;
  const radius = sp.trunk.radius * girth, phase = rng() * Math.PI * 2;
  const bend = height * sp.trunk.lean * range(rng, .8, 1.3);
  const outward = V(Math.cos(phase), 0, Math.sin(phase)), across = V(-outward.z, 0, outward.x);
  const stem = [0, .12, .29, .49, .69, .86, 1].map((t, i) => ({
    p: outward.clone().multiplyScalar(bend * (t * t * .85 + t * .15))
      .addScaledVector(across, Math.sin(t * Math.PI) * bend * .11)
      .add(V(0, i === 0 ? -.24 : height * t, 0)),
    radius: radius * (i === 0 ? 1.52 : .97 - t * .42),
  }));
  const crown = stem.at(-1)!.p;
  const count = Math.round(range(rng, ...sp.canopy.count));
  const fronds: PalmFrond[] = [];
  for (let i = 0; i < count; i++) {
    // Spiral phyllotaxis, uneven azimuth and leaf age avoid equal spoke rings.
    const angle = phase + i * 2.399963 + range(rng, -.22, .22);
    const young = i >= count - 3;
    const old = i < 3;
    const reach = range(rng, ...sp.canopy.size) * grow * (young ? .63 : range(rng, .87, 1.07));
    const direction = V(Math.cos(angle), 0, Math.sin(angle));
    const f: PalmFrond = {
      root: stem[5].p.clone().lerp(crown, (.976 + i / count * .022 - .86) / .14),
      direction, across: V(-direction.z, 0, direction.x),
      reach, rise: reach * (young ? range(rng, .52, .73) : range(rng, .28, .43)),
      droop: reach * (old ? range(rng, .46, .66) : young ? range(rng, -.14, -.04) : range(rng, .16, .40)),
      twist: range(rng, -.19, .19), width: reach * range(rng, .245, .30),
      radius: radius * (young ? .105 : .15), tone: old ? range(rng, .77, .91) : range(rng, .93, 1.10), pinnae: [],
    };
    for (const t of PINNA_T) for (const side of [-1, 1]) {
      const envelope = Math.pow(Math.sin(Math.PI * t), .68);
      f.pinnae.push({ t, side,
        length: f.width * envelope * range(rng, .83, 1.12),
        notch: range(rng, .56, .68),
        droop: range(rng, .20, .43), sweep: range(rng, .28, .48), tone: range(rng, .92, 1.06) });
    }
    fronds.push(f);
  }
  return { stem, fronds, height, phase };
}

/** One smooth upward arch flowing into a drooping tip. */
export function palmFrondPoint(f: PalmFrond, t: number): THREE.Vector3 {
  return f.root.clone().addScaledVector(f.direction, f.reach * t)
    .addScaledVector(f.across, Math.sin(t * Math.PI) * f.reach * f.twist)
    .add(V(0, Math.sin(t * Math.PI) * f.rise - t * t * f.droop, 0));
}

/** The distal rachis bends progressively. Shared ring vertices have identical
 * joints, and every pinna inherits the exact joint at its rachis attachment. */
export function palmFrondJoints(f: PalmFrond, t: number): { branch: WindJoint; twig: WindJoint } {
  const branch = windJoint(f.root, palmFrondPoint(f, 1), f.radius, .72);
  const hinge = palmFrondPoint(f, HINGE_T);
  const flex = THREE.MathUtils.clamp((t - HINGE_T) / (1 - HINGE_T), 0, 1);
  const twig = windJoint(hinge, palmFrondPoint(f, 1), f.radius * .45, .34 * flex);
  return { branch, twig };
}

class PalmMesh {
  positions: number[] = []; colors: number[] = []; sways: number[] = [];
  branches: number[] = []; twigs: number[] = [];
  tri(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, color: C3, sway: number,
    joints: Array<{ branch: WindJoint; twig: WindJoint }> = Array(3).fill({ branch: TREE_STEM, twig: NO_JOINT })): void {
    for (const [i, p] of [a, b, c].entries()) {
      this.positions.push(p.x, p.y, p.z); this.colors.push(...color); this.sways.push(sway);
      this.branches.push(...joints[i].branch); this.twigs.push(...joints[i].twig);
    }
  }
  /** Actual back faces retain the shared single-sided flora/depth materials.
   * Fronds must still exist when seen from below or in a grazing shadow. */
  sheet(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, color: C3,
    joints: Array<{ branch: WindJoint; twig: WindJoint }>): void {
    if (b.clone().sub(a).cross(c.clone().sub(a)).y < 0) { [b, c] = [c, b]; joints = [joints[0], joints[2], joints[1]]; }
    this.tri(a, b, c, color, 1, joints);
    this.tri(a, c, b, shade(color, .86), 1, [joints[0], joints[2], joints[1]]);
  }
  sweep(path: Knot[], sides: number, color: C3, joints = path.map(() => ({ branch: TREE_STEM, twig: NO_JOINT })), bark = false, caps = true): void {
    const rings = path.map((k, i) => {
      const tangent = path[Math.min(i + 1, path.length - 1)].p.clone().sub(path[Math.max(0, i - 1)].p).normalize();
      const q = new THREE.Quaternion().setFromUnitVectors(V(0, 1, 0), tangent);
      return Array.from({ length: sides }, (_, j) => V(Math.cos(j * 2 * Math.PI / sides) * k.radius,
        0, Math.sin(j * 2 * Math.PI / sides) * k.radius).applyQuaternion(q).add(k.p));
    });
    for (let s = 0; s < sides; s++) {
      const n = (s + 1) % sides;
      if (caps) this.tri(path[0].p, rings[0][s], rings[0][n], color, 0, Array(3).fill(joints[0]));
      for (let i = 0; i < path.length - 1; i++) {
        const c = bark ? shade(color, .88 + .10 * ((s + i * 2) % 4)) : color;
        this.tri(rings[i][s], rings[i + 1][s], rings[i][n], c, 0, [joints[i], joints[i + 1], joints[i]]);
        this.tri(rings[i][n], rings[i + 1][s], rings[i + 1][n], c, 0, [joints[i], joints[i + 1], joints[i + 1]]);
      }
      if (caps) this.tri(path.at(-1)!.p, rings.at(-1)![n], rings.at(-1)![s], color, 0, Array(3).fill(joints.at(-1)!));
    }
  }
  finish(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    for (const [name, data, itemSize] of [['position', this.positions, 3], ['color', this.colors, 3],
      ['sway', this.sways, 1], ['branchWind', this.branches, 4], ['twigWind', this.twigs, 4]] as const)
      g.setAttribute(name, new THREE.Float32BufferAttribute(data, itemSize));
    g.computeVertexNormals(); return g;
  }
}

export function buildPalm(rng: Rng, sp: SpeciesParams, ctx: BuildCtx, far: boolean): THREE.BufferGeometry {
  const plan = planPalm(rng, sp, ctx.maturity), mesh = new PalmMesh();
  // Retain every bend knot in the distant trunk as well as its connected flare.
  mesh.sweep(plan.stem, far ? 4 : 7, sp.palette.bark, undefined, true);
  if (!far && sp.motifs.barkBands) for (let i = 1; i < plan.stem.length; i++) {
    const a = plan.stem[i - 1], b = plan.stem[i], f = .56;
    const p = a.p.clone().lerp(b.p, f), direction = b.p.clone().sub(a.p).normalize();
    const radius = (a.radius + (b.radius - a.radius) * f) * 1.022;
    mesh.sweep([{ p, radius }, { p: p.clone().addScaledVector(direction, .065), radius: radius * .994 }],
      7, shade(sp.palette.bark, .77), undefined, false, false);
  }
  for (const f of plan.fronds) {
    const leaf = shade(sp.palette.leafSummer, f.tone);
    if (!far) {
      // The exposed petiole ends where the feather sheet begins. Past this
      // point its central ridge is part of that sheet, not an overlapping tube.
      const petioleT = [0, PINNA_T[0]];
      mesh.sweep(petioleT.map(t => ({ p: palmFrondPoint(f, t), radius: f.radius * (1 - t * .94) })),
        3, shade(leaf, .84), petioleT.map(t => palmFrondJoints(f, t)));
      const notchPoint = (p: Pinna): THREE.Vector3 => palmFrondPoint(f, p.t)
        .addScaledVector(f.across, p.side * p.length * p.notch)
        .addScaledVector(f.direction, p.length * .20).add(V(0, -p.length * .19, 0));
      for (const [index, pinna] of f.pinnae.entries()) {
        const nextT = RIB_T[RIB_T.indexOf(pinna.t) + 1];
        const root = palmFrondPoint(f, pinna.t), next = palmFrondPoint(f, nextT);
        const side = f.across.clone().multiplyScalar(pinna.side);
        const tip = root.clone().lerp(next, .68).addScaledVector(side, pinna.length).addScaledVector(f.direction, pinna.length * pinna.sweep)
          .add(V(0, -pinna.length * pinna.droop, 0));
        const shoulder = notchPoint(pinna), nextPinna = f.pinnae[index + 2];
        const nextShoulder = nextPinna ? notchPoint(nextPinna) : next;
        const a = palmFrondJoints(f, pinna.t), b = palmFrondJoints(f, nextT), middle = palmFrondJoints(f, (pinna.t + nextT) / 2);
        // One tessellated feather per frond: adjacent sections share both
        // their central ridge AND their notched rim. The protruding points
        // divide only the silhouette, with no overlapping shingle surfaces.
        mesh.sheet(root, shoulder, tip, shade(leaf, pinna.tone), [a, a, middle]);
        mesh.sheet(root, tip, nextShoulder, shade(leaf, pinna.tone * .96), [a, middle, b]);
        if (nextPinna) mesh.sheet(root, nextShoulder, next, shade(leaf, pinna.tone * .98), [a, b, b]);
      }
    } else {
      // A folded feather silhouette per complete leaf, preserving the shared
      // arch/tip, widths and wind hierarchy. No crown blobs or straight spokes.
      const ts = [0, .20, .50, .80, 1];
      const rows = ts.map((t, i) => {
        const center = palmFrondPoint(f, t);
        const width = Math.pow(Math.sin(Math.PI * t), .68) * f.width * (i % 2 ? .93 : 1.02);
        return { center, left: center.clone().addScaledVector(f.across, width).addScaledVector(f.direction, width * .30).add(V(0, -width * .29, 0)),
          right: center.clone().addScaledVector(f.across, -width).addScaledVector(f.direction, width * .35).add(V(0, -width * .25, 0)),
          joints: palmFrondJoints(f, t) };
      });
      for (let i = 0; i < rows.length - 1; i++) {
        const a = rows[i], b = rows[i + 1];
        for (const side of ['left', 'right'] as const) {
          if (i > 0) mesh.sheet(a.center, a[side], b.center, leaf, [a.joints, a.joints, b.joints]);
          if (i < rows.length - 2) mesh.sheet(a[side], b[side], b.center, shade(leaf, .94), [a.joints, b.joints, b.joints]);
        }
      }
    }
  }
  const g = mesh.finish();
  g.userData.palm = {
    stem: plan.stem.map(k => ({ p: k.p.toArray(), radius: k.radius })),
    crown: plan.stem.at(-1)!.p.toArray(), height: plan.height,
    fronds: plan.fronds.map(f => ({ root: f.root.toArray(), hinge: palmFrondPoint(f, HINGE_T).toArray(),
      tip: palmFrondPoint(f, 1).toArray(), radius: f.radius,
      branchJoint: palmFrondJoints(f, 1).branch, twigJoint: palmFrondJoints(f, 1).twig,
      pinnae: f.pinnae.map(p => ({ root: palmFrondPoint(f, p.t).toArray(), ...palmFrondJoints(f, p.t) })) })),
  };
  return g;
}
